import { produce } from 'immer';
import {
  ConversationsChannels,
  Message,
  SanitizeableChannels,
} from 'lib-shared-legacy-types';
import { Attributes, Tag } from 'sanitize-html';

import { SanitizationCategory } from 'app/utils/htmlSanitizer/types';

import { SANITIZE_PROFILE_DEFAULT } from './profiles/default';

const getLocationHost = () => {
  try {
    // Window.parent can rarely be null e.g. for contentWindow of an iframe that is already removed from the parent.
    if (window.parent !== null) {
      // Access to the window parent is not always available due to cross-origin restrictions
      // Hence we need to wrap this in a try/catch
      // At time of writing the only known case where this might happen is when
      // iframing the app in Testbox (partner integration)
      return window.parent.location.host;
    }
  } catch (error) {
    console.warn('Error getting location host', error);
  }
  return null;
};

export const addTargetAndRelToLinks = (
  htmlString?: string | null,
): string | null | undefined => {
  if (!htmlString) {
    return htmlString;
  }

  const div = document.createElement('div');
  // TODO: [TEAMS-12075] Investigate improper use of innerHTML
  // eslint-disable-next-line no-unsanitized/property
  div.innerHTML = htmlString;

  div.querySelectorAll('a').forEach((a) => {
    const href = a.getAttribute('href');

    if (!href) {
      return;
    }

    const host = getLocationHost();
    const includesHost = host && href.includes(host);
    const includesProtocol = href.includes('://');
    const isFeatureUrl = !!href.match('^(callto:|tel:|mailto:)');
    const isAllowedScheme = (
      SANITIZE_PROFILE_DEFAULT.allowedSchemes as string[]
    )?.some((scheme) => href.includes(`${scheme}:`));

    if (!isAllowedScheme && !includesProtocol && !isFeatureUrl) {
      a.setAttribute('href', `//${href}`);
    }

    if (!includesHost && !isFeatureUrl) {
      a.setAttribute('target', '_blank');
      a.setAttribute('rel', 'noopener noreferrer');
    }
  });

  return div.innerHTML;
};

export const removeModalLikeStyles = (
  tagName: string,
  attributes: Attributes,
): Tag => {
  return produce({ tagName, attribs: attributes }, (draft) => {
    const hasModalClass =
      attributes.class && attributes.class.includes('modal');
    if (hasModalClass) {
      // remove classname if 'modal' is present (bootstrap modal classname)
      draft.attribs.class = '';
    }
    if (
      (attributes.style && attributes.style.includes('position')) ||
      hasModalClass
    ) {
      const tempNode = document.createElement('div');
      tempNode.setAttribute('style', attributes.style);
      // reset position to relative, and reset commonly used positioning styles
      tempNode.style.position = 'relative';
      tempNode.style.top = '';
      tempNode.style.right = '';
      tempNode.style.bottom = '';
      tempNode.style.left = '';
      tempNode.style.margin = '';
      tempNode.style.transform = '';
      draft.attribs.style = tempNode.getAttribute('style') || '';
    }
  });
};

export const channelToSanitizationProfile: Record<
  SanitizeableChannels | 'notes' | 'bulk',
  SanitizationCategory
> = {
  adachat: 'messaging',
  digitalgeniuschat: 'messaging',
  kindlychat: 'messaging',
  contactform: 'email',
  email: 'email',
  notes: 'note',
  bulk: 'email',
  widgetchat: 'widgetchat',
  messenger: 'dixaMessenger',
  facebookmessenger: 'messaging',
  whatsapp: 'messaging',
  sms: 'messaging',
  trustpilotreviews: 'email',
  twitter: 'messaging',
};

export const getMessageSanitizationChannel = (
  message: Message,
): ConversationsChannels | 'notes' | null | undefined =>
  ['conversation:wrapup_message', 'conversation:wrapup_added'].includes(
    message.type,
  )
    ? 'notes'
    : message.channel;

export function removePlaceholderParagraphs(html: string): string {
  return html
    .replace(/<\/placeholderp>\n*<placeholderp/g, '</div><br /><div')
    .replace(/<placeholderp/g, '<div')
    .replace(/<\/placeholderp>/g, '</div>');
}
